import { forbidden, unauthorized } from "../errors.ts";
import type { Uuid } from "../types.ts";

/**
 * Política de autorização do Dream Calendar.
 *
 * Função pura e sem dependência de framework, para poder ser testada
 * exaustivamente e usada de forma idêntica em rotas HTTP, WebSocket e worker.
 *
 * Regra estrutural: o briefing exige que a proibição do cliente observador
 * exista no backend, e não apenas na ausência de botões. Isso é garantido pelo
 * tipo — um `PublicShareContext` **não possui** a capacidade de mutação, então
 * qualquer caminho de escrita rejeita esse contexto antes de tocar o banco.
 */

export type Capability =
  | "deliverable:read"
  | "deliverable:write"
  | "deliverable:delete"
  | "client:read"
  | "client:write"
  | "project:read"
  | "project:write"
  | "attachment:read"
  | "attachment:write"
  | "share:read"
  | "share:write"
  | "activity:read"
  | "user:read"
  | "user:write"
  | "social:read"
  | "social:write";

/** Escopo de dados acessível. Toda query recebe isto e filtra por ele. */
export interface AuthScope {
  organizationId: Uuid;
  /** Quando presente, o acesso é limitado a este cliente. */
  clientId: Uuid | null;
  /** Quando presente, o acesso é limitado a este projeto. */
  projectId: Uuid | null;
  /** Quando true, só entregas com `visible_to_client` são retornadas. */
  clientVisibleOnly: boolean;
}

export interface AdminContext {
  kind: "admin";
  userId: Uuid;
  organizationId: Uuid;
  email: string;
  scope: AuthScope;
}

export interface PublicShareContext {
  kind: "public_share";
  shareLinkId: Uuid;
  organizationId: Uuid;
  slug: string;
  scope: AuthScope;
}

export interface ClientViewContext {
  kind: "client_view";
  clientId: Uuid;
  organizationId: Uuid;
  handle: string;
  scope: AuthScope;
}

export interface AnonymousContext {
  kind: "anonymous";
}

export type AuthContext = AdminContext | PublicShareContext | ClientViewContext | AnonymousContext;

/**
 * Capacidades por tipo de contexto.
 *
 * O contexto público tem **apenas leitura**, e nem toda leitura: histórico de
 * atividades, links de compartilhamento e usuários ficam fora.
 */
const ADMIN_CAPABILITIES: readonly Capability[] = [
  "deliverable:read",
  "deliverable:write",
  "deliverable:delete",
  "client:read",
  "client:write",
  "project:read",
  "project:write",
  "attachment:read",
  "attachment:write",
  "share:read",
  "share:write",
  "activity:read",
  "user:read",
  "user:write",
  "social:read",
  "social:write",
];

const PUBLIC_SHARE_CAPABILITIES: readonly Capability[] = [
  "deliverable:read",
  "client:read",
  "project:read",
  "attachment:read",
];

export function isPublicReader(
  context: AuthContext,
): context is PublicShareContext | ClientViewContext {
  return context.kind === "public_share" || context.kind === "client_view";
}

export function capabilitiesFor(context: AuthContext): readonly Capability[] {
  switch (context.kind) {
    case "admin":
      return ADMIN_CAPABILITIES;
    case "public_share":
    case "client_view":
      return PUBLIC_SHARE_CAPABILITIES;
    case "anonymous":
      return [];
  }
}

export function can(context: AuthContext, capability: Capability): boolean {
  return capabilitiesFor(context).includes(capability);
}

/** Toda capacidade cujo nome não é de leitura. Usado pelo teste de rotas. */
export const MUTATING_CAPABILITIES: readonly Capability[] = ADMIN_CAPABILITIES.filter(
  (c) => !c.endsWith(":read"),
);

export function isMutating(capability: Capability): boolean {
  return !capability.endsWith(":read");
}

export function requireCapability(context: AuthContext, capability: Capability): void {
  if (context.kind === "anonymous") {
    throw unauthorized();
  }
  if (!can(context, capability)) {
    // Mensagem deliberadamente específica para o caso público: ajuda o
    // desenvolvedor e não vaza nada, porque o portal já sabe que é read-only.
    if (isPublicReader(context) && isMutating(capability)) {
      throw forbidden("O portal de acompanhamento é somente leitura.");
    }
    throw forbidden();
  }
}

/** Garante contexto administrativo. Portão único de toda rota de mutação. */
export function requireAdmin(context: AuthContext): AdminContext {
  if (context.kind === "anonymous") throw unauthorized();
  if (context.kind !== "admin") {
    throw forbidden("O portal de acompanhamento é somente leitura.");
  }
  return context;
}

/** Garante que o contexto pode ler, seja admin, link público ou visão do cliente. */
export function requireReader(
  context: AuthContext,
): AdminContext | PublicShareContext | ClientViewContext {
  if (context.kind === "anonymous") throw unauthorized();
  return context;
}

/**
 * Verifica se um recurso pertence ao escopo do contexto.
 *
 * Retorna boolean em vez de lançar, para que o chamador decida entre 404
 * (recurso individual — não vaza existência) e filtragem silenciosa (listas).
 */
export function isWithinScope(
  scope: AuthScope,
  resource: {
    organizationId: Uuid;
    clientId?: Uuid | null;
    projectId?: Uuid | null;
  },
): boolean {
  if (resource.organizationId !== scope.organizationId) return false;

  if (scope.clientId !== null) {
    if (resource.clientId === undefined || resource.clientId === null) return false;
    if (resource.clientId !== scope.clientId) return false;
  }

  if (scope.projectId !== null) {
    if (resource.projectId === undefined || resource.projectId === null) return false;
    if (resource.projectId !== scope.projectId) return false;
  }

  return true;
}

/**
 * Um recurso fora de escopo é reportado como inexistente.
 *
 * Responder 403 revelaria que o ID existe em outra organização, permitindo
 * enumeração. Este é o ponto central da defesa contra IDOR.
 */
export function assertWithinScope(
  scope: AuthScope,
  resource: {
    organizationId: Uuid;
    clientId?: Uuid | null;
    projectId?: Uuid | null;
  },
  resourceName: string,
): void {
  if (!isWithinScope(scope, resource)) {
    throw Object.assign(new Error(`${resourceName} não encontrado`), {
      name: "DomainError",
      code: "NOT_FOUND",
      status: 404,
    });
  }
}

/** Escopo total da organização, para administradores. */
export function adminScope(organizationId: Uuid): AuthScope {
  return {
    organizationId,
    clientId: null,
    projectId: null,
    clientVisibleOnly: false,
  };
}

/**
 * Escopo de um link público.
 *
 * `clientVisibleOnly` é sempre `true`: mesmo dentro do cliente correto, o
 * portal nunca vê entregas marcadas como internas.
 */
export function shareScope(
  organizationId: Uuid,
  clientId: Uuid,
  projectId: Uuid | null,
): AuthScope {
  return { organizationId, clientId, projectId, clientVisibleOnly: true };
}

/** Escopo da visão do cliente: só aquele cliente, só o que é visível a ele. */
export function clientViewScope(organizationId: Uuid, clientId: Uuid): AuthScope {
  return shareScope(organizationId, clientId, null);
}

/**
 * Um anexo é visível ao portal somente se ele **e** sua entrega forem públicos.
 * Marcar o anexo como público não basta se a entrega é interna.
 */
export function canViewAttachment(
  context: AuthContext,
  attachment: {
    isPublic: boolean;
    organizationId: Uuid;
    clientId: Uuid;
    projectId: Uuid | null;
  },
  deliverable: { visibleToClient: boolean },
): boolean {
  if (context.kind === "admin") {
    return isWithinScope(context.scope, attachment);
  }
  if (isPublicReader(context)) {
    if (!attachment.isPublic) return false;
    if (!deliverable.visibleToClient) return false;
    return isWithinScope(context.scope, attachment);
  }
  return false;
}

/** Canal de WebSocket que um contexto pode assinar. */
export function realtimeChannelsFor(context: AuthContext): string[] {
  switch (context.kind) {
    case "admin":
      return [`org:${context.organizationId}`];
    case "public_share": {
      const { scope } = context;
      if (scope.projectId !== null) return [`project:${scope.projectId}`];
      if (scope.clientId !== null) return [`client:${scope.clientId}`];
      return [];
    }
    case "client_view":
      return [`client:${context.clientId}`];
    case "anonymous":
      return [];
  }
}
